import type { AssetComponentKind, AssetRenewal, AssetType } from '@fos/shared/contracts/maintenance';
import type { BillingCycle } from '@fos/shared/contracts/services';
import { BILLING_CYCLE_SUFFIX } from '@fos/shared/labels';
import { AppWindow, AtSign, Box, Database, Globe, HardDrive, Link2, Lock, type LucideIcon, Server } from 'lucide-react';
import { formatDate, formatMoneyAuto } from '../../lib/format';
import type { Tone } from '../../ui/Status';

/** Ikony typów zasobów (Figma 08: strona = globe, aplikacja = app-window, domena = link, skrzynka = at-sign). */
export const ASSET_TYPE_ICONS: Record<AssetType, LucideIcon> = {
  website: Globe,
  app: AppWindow,
  domain: Link2,
  mailbox: AtSign,
  server: Server,
  other: Box,
};

/** Ikony elementów zasobu (Figma: karta zasobu → Elementy). */
export const COMPONENT_ICONS: Record<AssetComponentKind, LucideIcon> = {
  hosting: Server,
  domain: Link2,
  ssl: Lock,
  database: HardDrive,
  mailbox: AtSign,
  other: Database,
};

/**
 * Typy w modalu „Dodaj zasób” (Figma: Strona, Aplikacja, Domena, Skrzynka, Inne; Przegląd Fali 10: + Serwer — monitoring
 * serwera, czyli porty i meldunek, działa tylko w zasobie typu Serwer).
 */
export const FORM_ASSET_TYPES: readonly AssetType[] = ['website', 'app', 'domain', 'mailbox', 'server', 'other'];

/** „za 12 dni”, „jutro”, „dziś”, „3 dni po terminie”. */
export function daysLeftText(daysLeft: number): string {
  if (daysLeft > 1) return `za ${daysLeft} dni`;
  if (daysLeft === 1) return 'jutro';
  if (daysLeft === 0) return 'dziś';
  const n = -daysLeft;
  return `${n === 1 ? '1 dzień' : `${n} dni`} po terminie`;
}

/** Kolor terminu: po terminie czerwony, w ciągu 30 dni pomarańczowy (DESIGN §2: kolor tylko gdy znaczy). */
export function renewalTone(r: Pick<AssetRenewal, 'daysLeft' | 'overdue'>): Tone {
  if (r.overdue) return 'danger';
  if (r.daysLeft <= 30) return 'warning';
  return 'neutral';
}

export const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-fg-muted',
  accent: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

/** Kwota z cyklem: „1 990 zł / rok”, „490 zł / mies.”, „990 EUR”. */
export function amountWithCycle(amount: string, cycle: BillingCycle | null, currency = 'PLN'): string {
  const suffix = cycle ? BILLING_CYCLE_SUFFIX[cycle] : '';
  const text = formatMoneyAuto(amount, currency);
  return suffix ? `${text} ${suffix}` : text;
}

/** „Zenbox s55” (gdzie stoi). */
export function locationText(provider: string | null, server: string | null): string | null {
  const text = [provider, server].filter(Boolean).join(' ');
  return text || null;
}

export function dateText(date: string): string {
  return formatDate(date);
}

/** Adres strony dla „Otwórz” (bez schematu dopisujemy https). */
export function assetHref(address: string | null, name: string, type: AssetType): string | null {
  if (type === 'mailbox') return null;
  const raw = (address ?? name).trim();
  if (!raw || raw.includes('@')) return null;
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

/** Domyślny adres przy dodaniu zasobu: strona i aplikacja `https://…`, pozostałe bez zmian. */
export function defaultAddress(type: AssetType, name: string): string | null {
  const v = name.trim();
  if (!v) return null;
  if ((type === 'website' || type === 'app') && !/^https?:\/\//i.test(v) && !v.includes('@')) return `https://${v}`;
  return v;
}
